import { Trans, useTranslation } from "react-i18next";
import { isMobile } from "react-device-detect";
import { Col, Form, Progress, Row } from "antd";
import { toast } from "react-toastify";
import { RightOutlined, LoadingOutlined } from "@ant-design/icons";
import { ReleaseTypeEnum } from "shared/enums/ReleaseTypeEnum";
import {
  AboutArtistStep,
  FirstStep,
  ReleaseDataStep,
  StepContainer,
  SuccessfulStep,
} from "./steps";
import { useState } from "react";
import {
  createUserArtistSchedule,
  saveQuincyPlan,
} from "shared/service/StrmService";
import {
  Step,
  StepOrdered,
  FormValues,
  releaseTypeFieldName,
  formatReleasesToRequest,
} from "../interfaces";
import { useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import QuincyStep from "./steps/QuincyStep";
import "./new-releases-schedule.scss";

const firstStep: Step = {
  content: () => <FirstStep />,
  title: "pages.ReleasesSchedule.tipoLancamento",
};

const aboutArtistStep: Step = {
  content: (isVisible: boolean) => <AboutArtistStep isVisible={isVisible} />,
  title: "pages.ReleasesSchedule.sobreArtista",
};

const buildOrderedSteps = (
  releaseDataSteps: Step[],
  quincyStep: Step,
): StepOrdered[] => {
  const appendNewSteps = [
    firstStep,
    ...releaseDataSteps,
    aboutArtistStep,
    quincyStep,
  ];
  return appendNewSteps.map((x: any, index: number) => ({
    ...x,
    step: index + 1,
  }));
};

const NewReleasesScheduleWithReleasesFields = () => {
  const history = useHistory();
  const { t } = useTranslation();
  const { loggedUser } = useSelector((state: any) => state.users);
  const [form] = Form.useForm<FormValues>();

  const quincyStep: Step = {
    content: (isVisible: boolean) => (
      <QuincyStep isVisible={isVisible} />
    ),
    title: "pages.ReleasesSchedule.onlyQuincy.quincyIA",
  };

  const [steps, setSteps] = useState<StepOrdered[]>(() =>
    buildOrderedSteps([], quincyStep),
  );
  const [currentStep, setCurrentStep] = useState<StepOrdered>(steps[0]);
  const [loading, setLoading] = useState<boolean>(false);
  const [successful, setSuccessful] = useState<boolean>(false);

  const eps = Form.useWatch(["eps", "0", "tracksCount"], form);
  const albums = Form.useWatch(["albums", "0", "tracksCount"], form);
  const singles = Form.useWatch(["singles", "0", "tracksCount"], form);
  const isFirstStepInvalid = !albums && !eps && !singles;
  const artistMustAnswerQuiz = !loggedUser?.isReleasePlanningEnable;

  const getProgressPercent = () => (100 / steps.length) * currentStep.step;
  const isFirstStep = () => currentStep.step === 1;
  const isLastStep = () => currentStep.step === steps.length;

  const navigateToNextStep = () => {
    setCurrentStep(steps.find((step) => step.step === currentStep.step + 1)!);
  };
  const navigateToPreviousStep = () => {
    if (isFirstStep()) {
      return history.push("/releases-schedules");
    }
    setCurrentStep(steps.find((step) => step.step === currentStep.step - 1)!);
  };

  const submitForm = async () => {
    try {
      setLoading(true);

      const formValues = form.getFieldsValue() as FormValues;

      const request: any = {
        releases: [
          ...formatReleasesToRequest(
            ReleaseTypeEnum.Single,
            formValues.singles,
          ),
          ...formatReleasesToRequest(ReleaseTypeEnum.Album, formValues.albums),
          ...formatReleasesToRequest(ReleaseTypeEnum.EP, formValues.eps),
        ],
        aboutCreation: formValues.aboutCreation,
      };

      await createUserArtistSchedule(request);
      await saveQuincyPlan({
        aICurrentPlan: formValues.aICurrentPlan ?? "",
        aISuggestedPlan: formValues.aISuggestedPlan ?? "",
      });
      setSuccessful(true);
      toast.success<string>(t("pages.ReleasesSchedule.salvoSucesso"));
    } catch (error) {
      console.log(error);
      toast.error<string>(t("pages.ReleasesSchedule.erroAoSalvar"));
    } finally {
      setLoading(false);
    }
  };

  const createStepByReleaseType = (
    releaseType: ReleaseTypeEnum,
    releases: any[],
  ): Step[] => {
    const releaseTypeTitle: { [key in ReleaseTypeEnum]: string } = {
      [ReleaseTypeEnum.Single]: "titleSingle",
      [ReleaseTypeEnum.EP]: "titleEp",
      [ReleaseTypeEnum.Album]: "titleAlbum",
    };

    return releases?.map((_, index): Step => {
      const fieldName = releaseTypeFieldName[releaseType];
      return {
        title: "pages.ReleasesSchedule.sobreOsLancamentos",
        releaseType: releaseType,
        formListIndex: index,
        content: (isVisible: boolean) => (
          <ReleaseDataStep
            count={[index + 1, releases.length]}
            name={[fieldName, index.toString()]}
            title={`pages.ReleasesSchedule.steps.release.${releaseTypeTitle[releaseType]}`}
            isVisible={isVisible}
          />
        ),
      };
    });
  };

  const nextStep = async () => {
    if (isLastStep()) {
      return await submitForm();
    } else if (isFirstStep()) {
      const newSteps: Step[] = [];

      const releaseTypes = [
        ReleaseTypeEnum.Single,
        ReleaseTypeEnum.EP,
        ReleaseTypeEnum.Album,
      ];

      const releases = form.getFieldsValue(
        releaseTypes.map((type) => releaseTypeFieldName[type] as any),
      );

      releaseTypes.forEach((type) => {
        const release = releases[releaseTypeFieldName[type].toLowerCase()];
        if (release) {
          newSteps.push(...createStepByReleaseType(type, release));
        }
      });

      const orderedSteps = buildOrderedSteps(newSteps, quincyStep);

      setSteps(orderedSteps);
      setCurrentStep(orderedSteps[1]);
    } else {
      navigateToNextStep();
    }

    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  if (successful) {
    return <SuccessfulStep />;
  }

  return (
    <div className={`container-releases-schedule ${isMobile && "mobile"}`}>
      <div className="content">
        <Row justify="space-between" className="wrapper-progress-bar">
          <div className="text">{t(currentStep?.title ?? "")}</div>
          <div className="text">
            <Trans
              i18nKey={t("pages.ReleasesSchedule.progressBar.progress", {
                currentStep: currentStep?.step,
                totalSteps: steps.length,
              })}
            />
          </div>
          <Progress
            percent={getProgressPercent()}
            showInfo={false}
            strokeColor="#CECDFF"
          />
        </Row>
        <Form
          layout="vertical"
          form={form}
          initialValues={{
            singles: [],
            eps: [],
            albums: [],
            aICurrentPlan: "",
            aISuggestedPlan: "",
          }}
        >
          {steps.map((item, index) => (
            <div
              className="steps-content"
              key={index}
              hidden={item.step !== currentStep.step}
            >
              <StepContainer
                isVisible={item.step === currentStep.step}
                StepContent={item.content}
              />
            </div>
          ))}
        </Form>

        <Row className="btn-move-steps">
          <Col xs={24}>
            <button
              className="botao-roxo"
              style={{ width: "100%" }}
              onClick={async () => {
                try {
                  await form.validateFields();
                  nextStep();
                } catch (error) {
                  console.warn("invalid form", error);
                }
              }}
              disabled={loading || isFirstStepInvalid || artistMustAnswerQuiz}
            >
              {loading && <LoadingOutlined />}
              {isLastStep()
                ? t("pages.ReleasesSchedule.enviar")
                : t("pages.ReleasesSchedule.avancar")}
              <RightOutlined />
            </button>
          </Col>
          <Col xs={24}>
            <button
              className="botao-branco-vazado"
              style={{ width: "100%", marginBottom: 0 }}
              onClick={() => navigateToPreviousStep()}
              disabled={loading || artistMustAnswerQuiz}
            >
              {t("pages.ReleasesSchedule.voltar")}
            </button>
          </Col>
          {artistMustAnswerQuiz && (
            <Col>
              <p className="next-step-blocked-reason">
                {t("pages.ReleasesSchedule.preencherQuiz")}
              </p>
            </Col>
          )}
        </Row>
      </div>
    </div>
  );
};

export default NewReleasesScheduleWithReleasesFields;
